import React, { useState, useCallback, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Typography, 
  Checkbox,
  Card,
  Spin,
  message,
  Divider,
  AutoComplete
} from 'antd';
import { 
  CarOutlined, 
  SearchOutlined,
  ToolOutlined,
  NumberOutlined
} from '@ant-design/icons';
import { debounce } from 'lodash';
import { apiService } from '../../utils/apiService';
import { API_CONFIG } from '../../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Vehicle Step Component
 * Handles vehicle information with search and auto-population
 */
const VehicleStep = ({ form, formData, updateFormData, loading }) => {
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [trailer1Options, setTrailer1Options] = useState([]);
  const [trailer2Options, setTrailer2Options] = useState([]);
  const [loadingTrailer1, setLoadingTrailer1] = useState(false);
  const [loadingTrailer2, setLoadingTrailer2] = useState(false);

  /**
   * Create debounced vehicle search function
   */
  const debouncedVehicleSearch = useCallback(
    debounce(async (searchTerm, type = 'truck', fieldType = 'registration') => {
      if (!formData.selectedCompanyId) return;

      const setLoading = type === 'truck' ? setLoadingVehicles :
                        type === 'trailer1' ? setLoadingTrailer1 : setLoadingTrailer2;
      const setOptions = type === 'truck' ? setVehicleOptions :
                        type === 'trailer1' ? setTrailer1Options : setTrailer2Options;

      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append(fieldType, searchTerm);
        const url = `${API_CONFIG.VEHICLES.GET_COMPANY_VEHICLES}/${formData.selectedCompanyId}?${params}`;

        const resp = await apiService.get(url);
        const data = Array.isArray(resp) ? resp : resp?.data;
        setOptions(Array.isArray(data) ? data.slice(0, 10) : []);
      } catch (err) {
        console.error('Vehicle search error', err);
        message.error('Failed to fetch vehicles');
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500),
    [formData.selectedCompanyId]
  );

  /**
   * Search vehicles using the API with company-specific filtering
   */
  const searchVehicles = async (searchTerm, type = 'truck', fieldType = 'registration') => {
    const setLoading = type === 'truck' ? setLoadingVehicles : 
                     type === 'trailer1' ? setLoadingTrailer1 : setLoadingTrailer2;
    const setOptions = type === 'truck' ? setVehicleOptions : 
                      type === 'trailer1' ? setTrailer1Options : setTrailer2Options;

    // Ensure we have a selected company ID
    if (!formData.selectedCompanyId) {
      console.warn('No company selected for vehicle search');
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      // Build query parameters for optional filtering
      const queryParams = new URLSearchParams();
      
      // Add search term using the specific field type
      if (searchTerm) {
        queryParams.append(fieldType, searchTerm);
      }

      const queryString = queryParams.toString();
      const url = `${API_CONFIG.VEHICLES.GET_COMPANY_VEHICLES}/${formData.selectedCompanyId}${queryString ? `?${queryString}` : ''}`;
      
      console.log('🚛 Searching vehicles for company:', formData.selectedCompanyId, 'with field:', fieldType, 'term:', searchTerm);
      
      const response = await apiService.get(url);
      if (response && Array.isArray(response)) {
        setOptions(response.slice(0, 10));
      } else if (response?.data && Array.isArray(response.data)) {
        setOptions(response.data.slice(0, 10));
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error('Error searching company vehicles:', error);
      message.error('Failed to search vehicles');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle vehicle selection and auto-populate fields
   */
  const handleVehicleSelect = (vehicleId, type = 'truck') => {
    const sourceOptions = type === 'truck' ? vehicleOptions :
                         type === 'trailer1' ? trailer1Options : trailer2Options;
  
    const selectedVehicle = sourceOptions.find(v => v.id === vehicleId);
    if (!selectedVehicle) return;
  
    if (type === 'truck') {
      const truckValues = {
        vehicleId: vehicleId,
        truckRegistrationNumber: selectedVehicle.registration || '',
        truckFleetNumber: selectedVehicle.fleet || '',
        truckVinNumber: selectedVehicle.vin || '',
        truckMake: selectedVehicle.make || '',
        truckModel: selectedVehicle.model || '',
        truckYear: selectedVehicle.year || '',
        leaseType: selectedVehicle.leaseType || '',
        costCentreName: selectedVehicle.costCentreName || ''
      };
      updateFormData(truckValues);
      form.setFieldsValue(truckValues);
      return;
    }
  
    // trailers
    const trailerKey = type === 'trailer1' ? 'trailer1Data' : 'trailer2Data';
    const trailerValues = {
      [trailerKey]: {
        vehicleId: vehicleId,
        registration: selectedVehicle.registration || '',
        fleet: selectedVehicle.fleet || '',
        vin: selectedVehicle.vin || '',
        make: selectedVehicle.make || '',
        model: selectedVehicle.model || '',
        year: selectedVehicle.year || '',
        leaseType: selectedVehicle.leaseType || ''
      }
    };
    updateFormData(trailerValues);
  
    // keep antd Form in sync with what the user sees
    form.setFieldsValue({
      [`${type}Registration`]: selectedVehicle.registration || '',
      [`${type}Fleet`]: selectedVehicle.fleet || '',
      [`${type}Vin`]: selectedVehicle.vin || '',
      [`${type}Make`]: selectedVehicle.make || '',
      [`${type}Model`]: selectedVehicle.model || '',
      [`${type}Year`]: selectedVehicle.year || '',
    });
  };

  /**
   * Handle manual field changes
   */
  const handleFieldChange = (field, value, type = 'truck') => {
    if (type === 'truck') {
      const truckValues = {
        [field]: value
      };
      updateFormData(truckValues);
      form.setFieldsValue(truckValues);
      
      // Trigger search if it's a searchable field
      if (['truckRegistrationNumber', 'truckFleetNumber', 'truckVinNumber'].includes(field)) {
        debouncedVehicleSearch(value, 'truck');
      }
    } else {
      const trailerData = type === 'trailer1' ? 'trailer1Data' : 'trailer2Data';
      const fieldName = field.replace(/^(truck|trailer1|trailer2)/, '').toLowerCase();
      
      const trailerValues = {
        [trailerData]: {
          ...formData[trailerData],
          [fieldName]: value
        }
      };
      updateFormData(trailerValues);
      form.setFieldsValue(trailerValues);

      // Trigger search for trailer fields
      if (['registration', 'fleet', 'vin'].includes(fieldName)) {
        debouncedVehicleSearch(value, type);
      }
    }
  };

  /**
   * Handle trailer fault checkbox changes
   */
  const handleTrailerFaultChange = (trailerNumber, checked) => {
    const fieldName = `trailer${trailerNumber}Fault`;
    const trailerValues = {
      [fieldName]: checked
    };
    updateFormData(trailerValues);
    form.setFieldsValue(trailerValues);

    // Clear trailer data if unchecked
    if (!checked) {
      const trailerValues = {
        [`trailer${trailerNumber}Data`]: {}
      };
      updateFormData(trailerValues);
      form.setFieldsValue(trailerValues);
    }
  };

  /**
   * Render trailer section
   */
  const renderTrailerSection = (trailerNumber) => {
    const isEnabled = formData[`trailer${trailerNumber}Fault`];
    const trailerData = formData[`trailer${trailerNumber}Data`] || {};
    const options = trailerNumber === 1 ? trailer1Options : trailer2Options;
    const loading = trailerNumber === 1 ? loadingTrailer1 : loadingTrailer2;
    const type = `trailer${trailerNumber}`;

    if (!isEnabled) return null;

    return (
      <Card 
        title={`Trailer ${trailerNumber} Information`} 
        size="small" 
        style={{ marginTop: '16px' }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Item
                label="Trailer Registration"
                name={`trailer${trailerNumber}Registration`}
              >
                <AutoComplete
                  options={
                    options.map(v => ({
                      label: (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {v.registration} - {v.make} {v.model}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Fleet: {v.fleet} • VIN: {v.vin}
                          </Text>
                        </div>
                      ),
                      value: v.registration,
                      vehicle: v
                    }))
                  }
                  value={trailerData.registration || ''}
                  allowClear
                  placeholder="Enter registration"
                  filterOption={false}
                  notFoundContent={loading ? <Spin size="small" /> : null}
                  onSearch={val => { 
                    handleFieldChange('registration', val, type);
                    debouncedVehicleSearch(val, type, 'registration');
                  }}
                  onSelect={(val, option) => handleVehicleSelect(option.vehicle.id, type)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Item
                label="Fleet Number"
                name={`trailer${trailerNumber}Fleet`}
              >
                <AutoComplete
                  options={
                    options.map(v => ({
                      label: (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {v.fleet} - {v.make || "N/A"} {v.model || "N/A"}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Reg: {v.registration || "N/A"} • VIN: {v.vin || "N/A"}
                          </Text>
                        </div>
                      ),
                      value: v.fleet,
                      vehicle: v
                    }))
                  }
                  value={trailerData.fleet || ''}
                  allowClear
                  placeholder="Enter fleet number"
                  filterOption={false}
                  notFoundContent={loading ? <Spin size="small" /> : null}
                  onSearch={val => { 
                    handleFieldChange('fleet', val, type);
                    debouncedVehicleSearch(val, type, 'fleet');
                  }}
                  onSelect={(val, option) => handleVehicleSelect(option.vehicle.id, type)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Item
                label="VIN Number"
                name={`trailer${trailerNumber}Vin`}
              >
                <AutoComplete
                  options={
                    options.map(v => ({
                      label: (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {v.vin} - {v.make || "N/A"} {v.model || "N/A"}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Reg: {v.registration || "N/A"} • Fleet: {v.fleet || "N/A"}
                          </Text>
                        </div>
                      ),
                      value: v.vin,
                      vehicle: v
                    }))
                  }
                  value={trailerData.vin || ''}
                  allowClear
                  placeholder="Enter VIN"
                  filterOption={false}
                  notFoundContent={loading ? <Spin size="small" /> : null}
                  onSearch={val => { 
                    handleFieldChange('vin', val, type);
                    debouncedVehicleSearch(val, type, 'vin');
                  }}
                  onSelect={(val, option) => handleVehicleSelect(option.vehicle.id, type)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Item
                label="Make"
                name={`trailer${trailerNumber}Make`}
              >
                <Input
                  placeholder="Enter make"
                  value={trailerData.make || ''}
                  onChange={(e) => handleFieldChange('make', e.target.value, type)}
                />
              </Form.Item>
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Item
                label="Model"
                name={`trailer${trailerNumber}Model`}
              >
                <Input
                  placeholder="Enter model"
                  value={trailerData.model || ''}
                  onChange={(e) => handleFieldChange('model', e.target.value, type)}
                />
              </Form.Item>
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Item
                label="Year"
                name={`trailer${trailerNumber}Year`}
              >
                <Input
                  placeholder="Enter year"
                  value={trailerData.year || ''}
                  onChange={(e) => handleFieldChange('year', e.target.value, type)}
                />
              </Form.Item>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: '24px' }}>
        <CarOutlined style={{ marginRight: '8px' }} />
        Vehicle Information
      </Title>
      
      <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
        Enter truck details. Start typing to search for existing vehicles.
      </Text>

      {/* Truck Information */}
      <Card title="Truck Information" style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <Form.Item
                label="Registration Number"
                name="truckRegistrationNumber"
                rules={[{ required: true, message: 'Required' }]}
              >
                <AutoComplete
                  options={
                    vehicleOptions.map(v => ({
                      label: (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {v.registration} - {v.make || "N/A"} {v.model || "N/A"}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Fleet: {v.fleet || "N/A"} • VIN: {v.vin || "N/A"}
                          </Text>
                        </div>
                      ),
                      value: v.registration,
                      vehicle: v
                    }))
                  }
                  value={formData.truckRegistrationNumber}
                  allowClear
                  placeholder="Enter registration"
                  filterOption={false}
                  notFoundContent={loadingVehicles ? <Spin size="small" /> : null}
                  onSearch={val => { handleFieldChange('truckRegistrationNumber', val); debouncedVehicleSearch(val, 'truck', 'registration'); }}
                  onSelect={(val, option) => handleVehicleSelect(option.vehicle.id, 'truck')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <Form.Item label="Fleet Number" name="truckFleetNumber">
                <AutoComplete
                  options={
                    vehicleOptions.map(v => ({
                      label: (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {v.fleet} - {v.make || "N/A"} {v.model || "N/A"}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Reg: {v.registration || "N/A"} • VIN: {v.vin || "N/A"}
                          </Text>
                        </div>
                      ),
                      value: v.fleet,
                      vehicle: v
                    }))
                  }
                  value={formData.truckFleetNumber}
                  allowClear
                  placeholder="Enter fleet number"
                  filterOption={false}
                  notFoundContent={loadingVehicles ? <Spin size="small" /> : null}
                  onSearch={val => { handleFieldChange('truckFleetNumber', val); debouncedVehicleSearch(val, 'truck', 'fleet'); }}
                  onSelect={(val, option) => handleVehicleSelect(option.vehicle.id, 'truck')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <Form.Item label="VIN Number" name="truckVinNumber">
                <AutoComplete
                  options={
                    vehicleOptions.map(v => ({
                      label: (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {v.vin} - {v.make || "N/A"} {v.model || "N/A"}
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Reg: {v.registration || "N/A"} • Fleet: {v.fleet || "N/A"}
                          </Text>
                        </div>
                      ),
                      value: v.vin,
                      vehicle: v
                    }))
                  }
                  value={formData.truckVinNumber}
                  allowClear
                  placeholder="Enter VIN"
                  filterOption={false}
                  notFoundContent={loadingVehicles ? <Spin size="small" /> : null}
                  onSearch={val => { handleFieldChange('truckVinNumber', val); debouncedVehicleSearch(val, 'truck', 'vin'); }}
                  onSelect={(val, option) => handleVehicleSelect(option.vehicle.id, 'truck')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ flex: '1', minWidth: '150px' }}>
              <Form.Item
                label="Make"
                name="truckMake"
              >
                <Input
                  placeholder="Enter make"
                  value={formData.truckMake}
                  onChange={(e) => handleFieldChange('truckMake', e.target.value)}
                />
              </Form.Item>
            </div>
            <div style={{ flex: '1', minWidth: '150px' }}>
              <Form.Item
                label="Model"
                name="truckModel"
              >
                <Input
                  placeholder="Enter model"
                  value={formData.truckModel}
                  onChange={(e) => handleFieldChange('truckModel', e.target.value)}
                />
              </Form.Item>
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <Form.Item
                label="Year"
                name="truckYear"
              >
                <Input
                  placeholder="Enter year"
                  value={formData.truckYear}
                  onChange={(e) => handleFieldChange('truckYear', e.target.value)}
                />
              </Form.Item>
            </div>
            <div style={{ flex: '1', minWidth: '150px' }}>
              <Form.Item
                label="Mileage"
                name="mileage"
              >
                <Input
                  placeholder="Enter mileage"
                  value={formData.mileage}
                  onChange={(e) => handleFieldChange('mileage', e.target.value)}
                />
              </Form.Item>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <Form.Item
                label="Lease Type"
                name="leaseType"
              >
                <Input
                  placeholder="Enter lease type"
                  value={formData.leaseType}
                  onChange={(e) => handleFieldChange('leaseType', e.target.value)}
                />
              </Form.Item>
            </div>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <Form.Item
                label="Cost Center Name"
                name="costCentreName"
              >
                <Input
                  placeholder="Enter cost center name"
                  value={formData.costCentreName}
                  onChange={(e) => handleFieldChange('costCentreName', e.target.value)}
                />
              </Form.Item>
            </div>
          </div>
        </div>
      </Card>

      {/* Trailer Fault Checkboxes */}
      <Divider />
      <Title level={4}>
        <ToolOutlined style={{ marginRight: '8px' }} />
        Trailer Information
      </Title>
      
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '16px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <Checkbox
            checked={formData.trailer1Fault}
            onChange={(e) => handleTrailerFaultChange(1, e.target.checked)}
          >
            Trailer 1 Fault
          </Checkbox>
        </div>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <Checkbox
            checked={formData.trailer2Fault}
            onChange={(e) => handleTrailerFaultChange(2, e.target.checked)}
          >
            Trailer 2 Fault
          </Checkbox>
        </div>
      </div>

      {/* Render trailer sections if enabled */}
      {renderTrailerSection(1)}
      {renderTrailerSection(2)}
    </div>
  );
};

export default VehicleStep;
